import { css } from "@emotion/react";
import React from "react";

interface Props {
    deviceType: "ios" | "android";
}

interface Feature {
    title: string;
    isAiFeature?: boolean;
    values: (string | boolean)[];
}

const getCompetitorComparisonData = (deviceType: "ios" | "android"): Feature[] => [
    {
        title: "연 이용료",
        values: [
            deviceType === "ios" ? "43만 9천원" : "41만 2천원",
            deviceType === "ios" ? "29만 9천원" : "48만원",
            "90만원대",
            "50 ~ 60만원",
            "25만원",
        ],
    },
    { title: "미스고 AI 경매 길잡이\nDEEP", isAiFeature: true, values: [true, false, false, false, false] },
    { title: "AI 법령 도우미", isAiFeature: true, values: [true, false, false, false, false] },
    { title: "미스고 AI 챗봇", isAiFeature: true, values: [true, false, false, false, false] },
    { title: "매각결과 카톡 푸쉬 알람", isAiFeature: true, values: [true, true, false, false, false] },
    { title: "미스고 AI 경매 길잡이", isAiFeature: true, values: [true, true, false, false, false] },
    { title: "미스고 픽 전체 공개", isAiFeature: true, values: [true, true, false, false, false] },
    { title: "원터치 실시간 부동산 등기 열람", values: [true, true, false, false, false] },
    { title: "원터치 실시간 법인 등기 열람", values: [true, true, false, false, false] },
    { title: "원터치 실시간 토지 대장 열람", values: [true, true, false, false, false] },
    { title: "원터치 실시간 건축물 대장 열람", values: [true, true, false, false, false] },
    { title: "소유자 확인 가능", values: [true, true, false, false, false] },
    { title: "터치지도기반 필지 검색", values: [true, true, false, false, false] },
    { title: "국유지 열람", values: [true, true, false, false, false] },
    { title: "앱 모바일 유무(애플,삼성)", values: ["✅ 애플, 삼성", "삼성", false, false, false] },
];

const getMissgoPlanComparisonData = (deviceType: "ios" | "android"): { title: string; features: Feature[] }[] => [
    {
        title: "가격",
        features: [
            {
                title: "연 이용료",
                values: [
                    deviceType === "ios" ? "43만원" : "41만 2천원",
                    deviceType === "ios" ? "29만원" : "48만원",
                    deviceType === "ios" ? "27만원" : "24만원",
                ],
            },
        ],
    },
    {
        title: "기능",
        features: [
            { title: "미스고 AI 경매 길잡이\nDEEP", isAiFeature: true, values: [true, false, false] },
            { title: "AI 법령 도우미", isAiFeature: true, values: [true, false, false] },
            { title: "미스고 AI 챗봇", isAiFeature: true, values: [true, false, false] },
            { title: "매각결과 카톡 푸쉬 알람", isAiFeature: true, values: [true, true, false] },
            { title: "미스고 AI 경매 길잡이", isAiFeature: true, values: [true, true, false] },
            { title: "미스고 픽 전체 공개", isAiFeature: true, values: [true, true, false] },
            { title: "전국 경•공매 정보열람", values: [true, true, true] },
            { title: "터치 방식 데이터 맵", values: [true, true, true] },
            { title: "AI 추천", values: [true, true, true] },
            { title: "Go-Stop 투자분석", values: [true, true, true] },
            { title: "매일 업데이트되는 실거래가", values: [true, true, true] },
            { title: "입찰대리 전문가 매칭 (유료)", values: [true, true, true] },
            { title: "원터치 실시간 부동산 등기 열람 (유료)", values: [true, true, true] },
            { title: "기존 등기 열람 무제한 열람", values: [true, true, false] },
            { title: "실시간 등기열람권 매월 3장 무료 제공", values: [true, true, false] },
            { title: "도로확장 전체 데이터맵 1년 이용권 (제주 한정)", values: [true, true, false] },
            { title: "경매 캠퍼스 무제한 1달 수강", values: [true, true, false] },
            { title: "국•공유지 필터", values: [true, true, false] },
            { title: "주거/토지 수익 필터", values: [true, true, false] },
            { title: "상가 예상 수익률 Go-Stop", values: [true, true, false] },
            { title: "소유자 확인 이용권 - 기본 10장 (25년 5월부터)", values: [true, true, false] },
            { title: "토지대장•임야대장 무료 열람 - 하루 20장 무료", values: [true, true, false] },
            { title: "건축물대장 무료 열람 - 하루 20장 무료", values: [true, true, false] },
            { title: "VIP 전용 고객지원 서비스", values: [true, true, false] },
        ],
    },
];

const SubscriptionDesc = ({ deviceType }: Props) => {
    const competitorData = getCompetitorComparisonData(deviceType);
    const planData = getMissgoPlanComparisonData(deviceType);

    const renderCheckOrX = (value: string | boolean) => {
        if (typeof value === "boolean") {
            return <div>{value ? "✅" : "❌"}</div>;
        }
        return <div>{value}</div>;
    };

    const renderPrice = (value: string, isRed: boolean = false) => (
        <span style={{ fontSize: "25px", fontWeight: "700", color: isRed ? "#FF0000" : undefined }}>{value}</span>
    );

    return (
        <div css={rootBoxStyle}>
            <div css={titleBoxStyle}>📊 경쟁사 비교 📊</div>
            <div css={contentRootBoxStyle}>
                <table>
                    <colgroup>
                        <col width="auto" />
                        <col width="21%" />
                        <col width="21%" />
                        <col width="14%" />
                        <col width="14%" />
                        <col width="14%" />
                    </colgroup>
                    <thead>
                        <tr css={tableHeadStyle}>
                            <th />
                            <th style={{ color: "#F6494B" }}>미스고 VVIP Full</th>
                            <th style={{ color: "#0047FF" }}>미스고 VIP Full</th>
                            <th>A사</th>
                            <th>타사경매</th>
                            <th>저가경매</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td colSpan={6} css={tableSubTitleStyle}>
                                비교안내
                            </td>
                        </tr>
                        {competitorData.map((feature) => (
                            <tr className="basicTr" key={feature.title}>
                                <td>
                                    <div className={feature.isAiFeature ? "AiHighLight" : ""}>
                                        <div css={titleTextStyle}>{feature.title}</div>
                                    </div>
                                </td>
                                {feature.values.map((value, valueIdx) => (
                                    <td key={valueIdx}>{renderCheckOrX(value)}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div css={titleBoxStyle} style={{ marginTop: "24px" }}>
                📊
                <span className="purpleText">미스고</span>VIP 요금제 비교표
            </div>
            <div css={contentRootBoxStyle}>
                <table>
                    <colgroup>
                        <col width="30%" />
                        <col width="17.5%" />
                        <col width="17.5%" />
                        <col width="17.5%" />
                    </colgroup>
                    <thead>
                        <tr css={tableHeadStyle}>
                            <th />
                            <th style={{ color: "#F6494B" }}>VVIP Full</th>
                            <th style={{ color: "#0047FF" }}>VIP Full</th>
                            <th style={{ color: "#FF9500" }}>베이직</th>
                        </tr>
                    </thead>
                    {planData.map((section) => (
                        <tbody key={section.title}>
                            <tr>
                                <td colSpan={4} css={tableSubTitleStyle}>
                                    {section.title}
                                </td>
                            </tr>
                            {section.features.map((feature) => (
                                <tr className="basicTr" key={feature.title}>
                                    <td>
                                        <div className={feature.isAiFeature ? "AiHighLight" : ""}>
                                            <div css={titleTextStyle}>{feature.title}</div>
                                        </div>
                                    </td>
                                    {feature.values.map((value, valueIdx) => (
                                        <td key={valueIdx}>
                                            {section.title === "가격"
                                                ? renderPrice(value as string, valueIdx < 2)
                                                : renderCheckOrX(value)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    ))}
                </table>
            </div>
        </div>
    );
};

export default SubscriptionDesc;

const rootBoxStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding-top: 60px;
`;

const titleBoxStyle = css`
    text-align: center;
    padding-bottom: 24px;
    color: #0c002c;
    font-size: 24px;
    font-weight: 700;
    line-height: 34px;

    .purpleText {
        color: #5f0080;
        margin-left: 8px;
        margin-right: 8px;
    }
`;

const contentRootBoxStyle = css`
    border: 0.6px solid #c9c9c9;
    border-radius: 4px;
    width: calc(100% - 28px);
    overflow-x: auto;
    box-sizing: border-box;
    table {
        width: 100%;
        min-width: 600px;
        border-collapse: collapse;
        vertical-align: middle;
        tbody {
            vertical-align: middle;
        }
        td {
            vertical-align: middle;
        }
        .basicTr > td {
            &:first-of-type {
                text-align: left;
                padding-left: 14px;
            }
            box-sizing: border-box;
            border-bottom: 0.6px solid #c9c9c9;
            padding: 8px 4px;
            text-align: center;
            color: #0c002c;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
        }

        tbody:last-of-type .basicTr:last-of-type > td {
            border-bottom: none;
        }

        .AiHighLight {
            width: 190px;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 4px 0;
            background-color: #000;
            color: #fff !important;
            border-radius: 5px;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;

            & > div {
                color: #fff;
            }
        }
    }
`;

const tableHeadStyle = css`
    text-align: center;
    color: #0047ff;
    background-color: #dff0ff;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    vertical-align: middle;
    th {
        padding: 4px;
        box-sizing: border-box;
    }
    th:first-of-type {
        border-radius: 4px 0 0 4px;
    }
    th:last-of-type {
        border-radius: 0 4px 4px 0;
        word-break: break-word;
        white-space: pre-wrap;
    }
`;

const tableSubTitleStyle = css`
    text-align: left;
    color: #0c002c;
    font-size: 16px;
    font-weight: 700;
    line-height: 26px;
    padding: 8px 14px;
`;

const titleTextStyle = css`
    display: inline-flex;
    align-items: center;
    position: relative;
    color: #0c002c;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    gap: 4px;
`;
